// next-app/src/components/survey/vehicle-selector.tsx
'use client';

import { useFormContext, useController } from 'react-hook-form';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { Vehicle } from '@/types';

interface VehicleSelectorProps {
  vehicles: Vehicle[];
  name: string;
  title?: string;
  description?: string;
  disabled?: boolean;
}

/**
 * Vehicle selection grid for surveys.
 * Allows multiple vehicle selection with visual cards.
 */
export function VehicleSelector({
  vehicles,
  name,
  title = 'Vehicles of Interest',
  description = 'Select any vehicles you are interested in learning more about',
  disabled,
}: VehicleSelectorProps) {
  const { control } = useFormContext();
  const {
    field: { value = [], onChange },
  } = useController({
    control,
    name,
    defaultValue: [],
  });

  const selectedIds = value as number[];

  const toggleVehicle = (vehicleId: number) => {
    if (disabled) return;

    if (selectedIds.includes(vehicleId)) {
      onChange(selectedIds.filter((id) => id !== vehicleId));
    } else {
      onChange([...selectedIds, vehicleId]);
    }
  };

  const sortedVehicles = [...vehicles].sort((a, b) => a.sort_order - b.sort_order);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">{title}</h2>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {sortedVehicles.map((vehicle) => {
          const isSelected = selectedIds.includes(vehicle.vehicle_id);

          return (
            <Card
              key={vehicle.vehicle_id}
              className={cn(
                'cursor-pointer transition-colors hover:bg-accent',
                isSelected && 'border-primary bg-accent',
                disabled && 'cursor-not-allowed opacity-50'
              )}
              onClick={() => toggleVehicle(vehicle.vehicle_id)}
            >
              <CardContent className="flex items-center gap-3 p-4">
                <Checkbox
                  checked={isSelected}
                  disabled={disabled}
                  onCheckedChange={() => toggleVehicle(vehicle.vehicle_id)}
                  onClick={(e) => e.stopPropagation()}
                />
                <div className="flex-1">
                  <Label className="cursor-pointer font-medium">
                    {vehicle.model_year} {vehicle.name}
                  </Label>
                  {vehicle.is_black_label && (
                    <p className="text-xs text-muted-foreground">Black Label</p>
                  )}
                </div>
                {vehicle.image_url && (
                  <img
                    src={vehicle.image_url}
                    alt={vehicle.name}
                    className="h-12 w-20 rounded object-contain"
                  />
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {selectedIds.length > 0 && (
        <p className="text-sm text-muted-foreground">
          {selectedIds.length} vehicle{selectedIds.length !== 1 ? 's' : ''} selected
        </p>
      )}
    </div>
  );
}
